/**
 * useTaskToTimeBoxDrag Hook
 *
 * 處理從 Planned Layer 拖曳任務到日曆創建 TimeBox 的邏輯。
 *
 * 功能：
 * - 接收外部拖曳的任務數據
 * - 計算放置位置對應的時間
 * - 支持 15 分鐘對齊
 * - 顯示放置預覽
 * - 支持桌面（滑鼠）和觸控設備
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Task } from '../types/task';
import { DISPLAY_CONFIG, DRAG_CONFIG } from '../utils/calendarHelper';
import { eventBus, EVENTS } from '../utils/eventBus';

// 拖曳數據類型（從 PlannedTaskCard 傳遞）
export interface TaskDragData {
  type: 'planned-task';
  taskId: string;
  estimatedTime: number; // 預估時間（分鐘）
}

// 放置預覽狀態
export interface DropPreviewState {
  isActive: boolean;
  dayIndex: number;
  startTime: Date;
  endTime: Date;
  taskId: string;
}

export interface UseTaskToTimeBoxDragOptions {
  /** 每小時高度（像素） */
  hourHeight?: number;
  /** 開始顯示的小時 */
  startHour?: number;
  /** 結束顯示的小時 */
  endHour?: number;
  /** 時間欄寬度 */
  timeColumnWidth?: number;
  /** 日期數組（用於計算放置的日期） */
  days?: Date[];
  /** 創建 TimeBox 的回調 */
  onCreateTimeBox?: (task: Task, startTime: Date, endTime: Date) => Promise<void>;
  /** 將任務添加到既有 TimeBox 的回調 */
  onAddTaskToTimeBox?: (timeBoxId: string, taskId: string) => Promise<void>;
  /** 獲取任務詳情的函數（通過 taskId） */
  getTaskById?: (taskId: string) => Task | undefined;
  /** 是否啟用 */
  enabled?: boolean;
}

export interface UseTaskToTimeBoxDragReturn {
  // 狀態
  dropPreview: DropPreviewState | null;
  isDragOver: boolean;
  isDraggingTouch: boolean; // 觸控拖曳中
  dropTargetTimeBox: string | null; // 當前懸停的 TimeBox ID

  // 滑鼠事件處理器（綁定到日曆容器）
  handleDragOver: (e: React.DragEvent, containerRect: DOMRect, dayIndex?: number) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, containerRect: DOMRect, dayIndex?: number) => Promise<void>;

  // TimeBox 拖曳事件處理器
  handleDragOverTimeBox: (timeBoxId: string, e: React.DragEvent) => void;
  handleDragLeaveTimeBox: (e: React.DragEvent) => void;
  handleDropOnTimeBox: (timeBoxId: string, e: React.DragEvent) => Promise<void>;

  // 觸控事件處理器
  handleTouchMove: (e: TouchEvent) => void;
  handleTouchEnd: (e: TouchEvent) => void;
  startTouchDrag: (task: Task, touch: Touch, containerRect: DOMRect) => void;

  // 工具函數
  getDropPreviewStyle: () => React.CSSProperties | null;
  resetDropPreview: () => void;
}

/**
 * 將像素位置轉換為時間（對齊到 15 分鐘）
 */
function pixelToTime(
  pixelY: number,
  containerTop: number,
  hourHeight: number,
  startHour: number,
  baseDate: Date
): Date {
  const relativeY = pixelY - containerTop;
  const totalMinutes = (relativeY / hourHeight) * 60 + startHour * 60;

  // 對齊到 15 分鐘
  const snapMinutes = DRAG_CONFIG.SNAP_INTERVAL_MINUTES;
  const snappedMinutes = Math.round(totalMinutes / snapMinutes) * snapMinutes;

  const hours = Math.floor(snappedMinutes / 60);
  const minutes = snappedMinutes % 60;

  const result = new Date(baseDate);
  result.setHours(hours, minutes, 0, 0);

  return result;
}

/**
 * 解析拖曳數據
 */
function parseTaskDragData(e: React.DragEvent): TaskDragData | null {
  try {
    const data = e.dataTransfer.getData('application/json');
    if (!data) return null;

    const parsed = JSON.parse(data);
    if (parsed.type === 'planned-task') {
      return parsed as TaskDragData;
    }
    return null;
  } catch {
    return null;
  }
}

export const useTaskToTimeBoxDrag = (
  options: UseTaskToTimeBoxDragOptions = {}
): UseTaskToTimeBoxDragReturn => {
  const {
    hourHeight = DISPLAY_CONFIG.HOUR_HEIGHT,
    startHour = DISPLAY_CONFIG.START_HOUR,
    endHour = DISPLAY_CONFIG.END_HOUR,
    timeColumnWidth = DISPLAY_CONFIG.TIME_COLUMN_WIDTH,
    days,
    onCreateTimeBox,
    onAddTaskToTimeBox,
    getTaskById,
    enabled = true,
  } = options;

  const [dropPreview, setDropPreview] = useState<DropPreviewState | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDraggingTouch, setIsDraggingTouch] = useState(false);
  const [dropTargetTimeBox, setDropTargetTimeBox] = useState<string | null>(null);
  const lastDragOverTime = useRef<number>(0);

  // 觸控拖曳狀態
  const touchDragTask = useRef<Task | null>(null);
  const touchDragContainer = useRef<DOMRect | null>(null);
  const isTouchDragging = useRef(false);

  // 處理拖曳進入/經過
  const handleDragOver = useCallback(
    (e: React.DragEvent, containerRect: DOMRect, dayIndex: number = 0) => {
      if (!enabled) return;

      // 檢查是否是 planned-task 類型
      if (!e.dataTransfer.types.includes('application/json')) {
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';

      // 節流：每 50ms 更新一次預覽
      const now = Date.now();
      if (now - lastDragOverTime.current < 50) return;
      lastDragOverTime.current = now;

      setIsDragOver(true);

      // 計算放置時間
      const baseDate = days?.[dayIndex] || new Date();
      const startTime = pixelToTime(
        e.clientY,
        containerRect.top,
        hourHeight,
        startHour,
        baseDate
      );

      // 從拖曳數據獲取預估時間（使用預設值）
      // 注意：dragOver 時無法讀取 dataTransfer 數據，使用預設 60 分鐘
      const durationMinutes = 60;
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + durationMinutes);

      // 確保不超過結束時間
      const maxEndHour = endHour + 1;
      if (endTime.getHours() > maxEndHour) {
        endTime.setHours(maxEndHour, 0, 0, 0);
      }

      setDropPreview({
        isActive: true,
        dayIndex,
        startTime,
        endTime,
        taskId: '', // dragOver 時無法獲取
      });
    },
    [enabled, days, hourHeight, startHour, endHour]
  );

  // 處理拖曳離開
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // 避免子元素觸發 leave
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;

    if (currentTarget.contains(relatedTarget)) {
      return;
    }

    setIsDragOver(false);
    setDropPreview(null);
  }, []);

  // 處理放置
  const handleDrop = useCallback(
    async (e: React.DragEvent, containerRect: DOMRect, dayIndex: number = 0) => {
      if (!enabled) return;

      e.preventDefault();

      // 解析拖曳數據
      const dragData = parseTaskDragData(e);
      if (!dragData) {
        setDropPreview(null);
        setIsDragOver(false);
        return;
      }

      // 獲取任務詳情
      const task = getTaskById?.(dragData.taskId);
      if (!task) {
        console.warn('找不到任務:', dragData.taskId);
        setDropPreview(null);
        setIsDragOver(false);
        return;
      }

      // 計算放置時間
      const baseDate = days?.[dayIndex] || new Date();
      const startTime = pixelToTime(
        e.clientY,
        containerRect.top,
        hourHeight,
        startHour,
        baseDate
      );

      // 使用任務的預估時間或預設 60 分鐘
      const durationMinutes = task.estimatedTime || 60;
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + durationMinutes);

      // 確保不超過結束時間
      const maxEndHour = endHour + 1;
      if (endTime.getHours() > maxEndHour) {
        endTime.setHours(maxEndHour, 0, 0, 0);
      }

      // 清除預覽
      setDropPreview(null);
      setIsDragOver(false);

      // 調用創建 TimeBox 回調
      if (onCreateTimeBox) {
        await onCreateTimeBox(task, startTime, endTime);
        // 發佈 UI 事件用於 Tip 自動標記
        eventBus.publish(EVENTS.UI.TASK_DROPPED_TO_CALENDAR);
      }
    },
    [enabled, days, hourHeight, startHour, endHour, getTaskById, onCreateTimeBox]
  );

  // 獲取預覽樣式
  const getDropPreviewStyle = useCallback((): React.CSSProperties | null => {
    if (!dropPreview || !dropPreview.isActive) return null;

    const { startTime, endTime, dayIndex } = dropPreview;

    // 計算垂直位置
    const startHourValue = startTime.getHours() + startTime.getMinutes() / 60;
    const endHourValue = endTime.getHours() + endTime.getMinutes() / 60;

    const top = (startHourValue - startHour) * hourHeight;
    const height = (endHourValue - startHourValue) * hourHeight;

    // 計算水平位置（如果是多日視圖）
    let left: string | undefined;
    let width: string | undefined;

    if (days && days.length > 1) {
      const columnCount = days.length;
      const containerWidth = `calc(100% - ${timeColumnWidth}px)`;
      left = `calc(${timeColumnWidth}px + (${containerWidth} * ${dayIndex} / ${columnCount}))`;
      width = `calc(${containerWidth} / ${columnCount} - 8px)`;
    } else {
      left = `${timeColumnWidth + 4}px`;
      width = `calc(100% - ${timeColumnWidth + 8}px)`;
    }

    return {
      position: 'absolute',
      top: `${top}px`,
      left,
      width,
      height: `${Math.max(height, DISPLAY_CONFIG.MIN_TIMEBOX_HEIGHT)}px`,
      marginLeft: '4px',
      marginRight: '4px',
      pointerEvents: 'none',
    };
  }, [dropPreview, hourHeight, startHour, timeColumnWidth, days]);

  // 重置預覽
  const resetDropPreview = useCallback(() => {
    setDropPreview(null);
    setIsDragOver(false);
    setIsDraggingTouch(false);
  }, []);

  // === 觸控事件處理 ===

  // 開始觸控拖曳（由 PlannedTaskCard 調用）
  const startTouchDrag = useCallback((task: Task, touch: Touch, containerRect: DOMRect) => {
    if (!enabled) return;

    touchDragTask.current = task;
    touchDragContainer.current = containerRect;
    isTouchDragging.current = true;
    setIsDraggingTouch(true);

    // 初始化預覽（使用觸控點位置）
    const baseDate = days?.[0] || new Date();
    const startTime = pixelToTime(
      touch.clientY,
      containerRect.top,
      hourHeight,
      startHour,
      baseDate
    );

    const durationMinutes = task.estimatedTime || 60;
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + durationMinutes);

    // 確保不超過結束時間
    const maxEndHour = endHour + 1;
    if (endTime.getHours() > maxEndHour) {
      endTime.setHours(maxEndHour, 0, 0, 0);
    }

    setDropPreview({
      isActive: true,
      dayIndex: 0,
      startTime,
      endTime,
      taskId: task.id,
    });
  }, [enabled, days, hourHeight, startHour, endHour]);

  // 觸控移動
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isTouchDragging.current || !touchDragContainer.current || !touchDragTask.current) return;

    // 只處理單點觸控
    if (e.touches.length !== 1) return;

    // 阻止頁面滾動
    e.preventDefault();

    const touch = e.touches[0];
    const containerRect = touchDragContainer.current;
    const task = touchDragTask.current;

    // 計算放置的日期索引
    let dayIndex = 0;
    if (days && days.length > 1) {
      // 計算水平位置對應的日期
      const relativeX = touch.clientX - containerRect.left - timeColumnWidth;
      const dayWidth = (containerRect.width - timeColumnWidth) / days.length;
      dayIndex = Math.floor(relativeX / dayWidth);
      dayIndex = Math.max(0, Math.min(dayIndex, days.length - 1));
    }

    const baseDate = days?.[dayIndex] || new Date();
    const startTime = pixelToTime(
      touch.clientY,
      containerRect.top,
      hourHeight,
      startHour,
      baseDate
    );

    const durationMinutes = task.estimatedTime || 60;
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + durationMinutes);

    // 確保不超過結束時間
    const maxEndHour = endHour + 1;
    if (endTime.getHours() > maxEndHour) {
      endTime.setHours(maxEndHour, 0, 0, 0);
    }

    setDropPreview({
      isActive: true,
      dayIndex,
      startTime,
      endTime,
      taskId: task.id,
    });
  }, [days, hourHeight, startHour, endHour, timeColumnWidth]);

  // 觸控結束
  const handleTouchEnd = useCallback(async (e: TouchEvent) => {
    // 處理 touchcancel
    if (e.type === 'touchcancel') {
      touchDragTask.current = null;
      touchDragContainer.current = null;
      isTouchDragging.current = false;
      setIsDraggingTouch(false);
      setDropPreview(null);
      return;
    }

    if (!isTouchDragging.current || !touchDragTask.current || !dropPreview) return;

    const task = touchDragTask.current;

    // 清理狀態
    const finalPreview = { ...dropPreview };
    touchDragTask.current = null;
    touchDragContainer.current = null;
    isTouchDragging.current = false;
    setIsDraggingTouch(false);
    setDropPreview(null);

    // 調用創建 TimeBox 回調
    if (onCreateTimeBox) {
      await onCreateTimeBox(task, finalPreview.startTime, finalPreview.endTime);
    }
  }, [dropPreview, onCreateTimeBox]);

  // 全局事件監聽器（觸控事件）
  useEffect(() => {
    if (!enabled) return;

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (isTouchDragging.current) {
        handleTouchMove(e);
      }
    };

    const handleGlobalTouchEnd = (e: TouchEvent) => {
      if (isTouchDragging.current) {
        handleTouchEnd(e);
      }
    };

    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);
    document.addEventListener('touchcancel', handleGlobalTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchEnd);
    };
  }, [enabled, handleTouchMove, handleTouchEnd]);

  // === TimeBox 拖曳事件處理 ===

  // 拖曳到既有 TimeBox 上
  const handleDragOverTimeBox = useCallback((timeBoxId: string, e: React.DragEvent) => {
    if (!enabled) return;

    // 檢查是否是 planned-task 類型
    if (!e.dataTransfer.types.includes('application/json')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';

    setDropTargetTimeBox(timeBoxId);
  }, [enabled]);

  // 拖曳離開 TimeBox
  const handleDragLeaveTimeBox = useCallback((e: React.DragEvent) => {
    // 避免子元素觸發 leave
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;

    if (currentTarget.contains(relatedTarget)) {
      return;
    }

    setDropTargetTimeBox(null);
  }, []);

  // 在 TimeBox 上釋放任務
  const handleDropOnTimeBox = useCallback(async (timeBoxId: string, e: React.DragEvent) => {
    if (!enabled) return;

    e.preventDefault();
    e.stopPropagation();
    // stopPropagation 會阻止容器層 dragLeave 觸發，需手動清除所有拖曳狀態

    // 解析拖曳數據
    const dragData = parseTaskDragData(e);
    if (!dragData) {
      setDropTargetTimeBox(null);
      setDropPreview(null);
      setIsDragOver(false);
      return;
    }

    // 清除所有拖曳狀態
    setDropTargetTimeBox(null);
    setDropPreview(null);
    setIsDragOver(false);

    // 調用添加任務到 TimeBox 回調
    if (onAddTaskToTimeBox) {
      await onAddTaskToTimeBox(timeBoxId, dragData.taskId);
    }
  }, [enabled, onAddTaskToTimeBox]);

  return {
    dropPreview,
    isDragOver,
    isDraggingTouch,
    dropTargetTimeBox,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragOverTimeBox,
    handleDragLeaveTimeBox,
    handleDropOnTimeBox,
    handleTouchMove,
    handleTouchEnd,
    startTouchDrag,
    getDropPreviewStyle,
    resetDropPreview,
  };
};

export default useTaskToTimeBoxDrag;
